import React, {
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  useRef,
  useState,
} from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import Link from '@mui/material/Link'

import { newPermissionInfo } from '../../../tools/mockData'
import { UsagePermissionResponseDto } from '../../api/kmsapiclient'
import { addUsagePermissionAsync } from '../../api/usagePermissionApi'
import { ADD_USAGE_PERMISSION } from '../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import { addUsagePermissionSuccess } from '../../redux/actions/usagePermissionsActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import {
  IdentityState,
  UsagePermissionsState,
  UserPermissionsState,
} from '../../redux/reducers/initialState'
import { recordsPerPage } from '../../utils'
import DisplayMoreDialog from '../common/DisplayMoreDialog'
import ExpandableRow from '../common/ExpandableRow'
import GenericModal from '../common/GenericModal'
import TOverlayTrigger from '../common/OverlayTrigger'
import { usePermissions } from '../common/permission/usePermissions'
import TBody from '../common/TBody'
import TDeleteLink from '../common/TDeleteLink'
import TEditLink from '../common/TEditLink'
import TFooter from '../common/TFooter'
import TRowEmpty from '../common/TRowEmpty'
import { isScreenSizeSmall } from '../common/utils'

import AddUsagePermissionsForm, {
  AddUsagePermissionsFormPropsRef,
} from './AddUsagePermissionsForm'
import { SearchUsagePermissionsInfo, UsagePermissionsInfo } from './types'

import '../../css/common/KeyList.css'

type UsagePermissionsProps = {
  usagePermissions: UsagePermissionsInfo[]
  onDeleteClick: (usagePermission: UsagePermissionsInfo) => void
  onSearchClick: () => void
  onLoadMoreClick: () => void
  handleChange: ChangeEventHandler<HTMLInputElement>
  handleBlur: FocusEventHandler<HTMLInputElement>
  loading: boolean
  usagePermissionsExists: boolean
  searchPermissionsRequest: SearchUsagePermissionsInfo
  setDataChanged?: Dispatch<React.SetStateAction<boolean>>
}

const UsagePermissionsList = ({
  usagePermissions,
  onLoadMoreClick,
  onDeleteClick,
  onSearchClick,
  loading,
  handleChange,
  handleBlur,
  searchPermissionsRequest,
  usagePermissionsExists,
}: UsagePermissionsProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { checkPermissions } = usePermissions()

  const usagePermissionsPerPage = recordsPerPage
  const totalPages = Math.ceil(
    usagePermissions.length / usagePermissionsPerPage,
  )
  const [pageNum, setPageNum] = useState<number>(1)
  const [formData, setFormData] =
    useState<UsagePermissionsInfo>(newPermissionInfo)
  const [formReady, setFormReady] = useState(false)
  const [dataChanged, setDataChanged] = useState(false)
  const refFormData = useRef<UsagePermissionsInfo>()
  const refValidateData = useRef<AddUsagePermissionsFormPropsRef>()
  refFormData.current = formData

  const showingText = `Showing ${
    (pageNum - 1) * usagePermissionsPerPage + 1
  }  to  ${Math.min(
    usagePermissions.length,
    pageNum * usagePermissionsPerPage,
  )}  of ${usagePermissions.length}`

  const identity: IdentityState | null = useSelector(
    (state: IRootState) => state.identity,
  )
  const userPermissions: UserPermissionsState | null = useSelector(
    (state: IRootState) => state.userPermissions,
  )
  const storedUsagePermissions: UsagePermissionsState = useSelector(
    (state: IRootState) => state.usagePermissions,
  )

  const disableLoadMore = () => {
    return (
      storedUsagePermissions.continuationToken == null ||
      storedUsagePermissions.continuationToken === ''
    )
  }

  const isAuthorized = identity != null && userPermissions != null

  const disableGetUsagePermissions =
    loading || !(isAuthorized && userPermissions.hasGetPermissionsAccess)
  const disableAddUsagePermissions =
    loading || !(isAuthorized && userPermissions.hasAddPermissionsAccess)
  const disableEditUsagePermissions = !(
    isAuthorized && userPermissions.hasUpdatePermissionsAccess
  )
  const disableDeleteUsagePermissions = !(
    isAuthorized && userPermissions.hasDeletePermissionsAccess
  )

  const handlePageChange = (event, page) => setPageNum(page)

  const handleSaveAddUsagePermissionModal = async () => {
    if (refValidateData.current?.entityData !== undefined) {
      await addUsagePermission(refValidateData.current?.entityData)
      resetAddUsagePermission()
    }
  }

  const handleCloseAddUsagePermissionModal = () => {
    resetAddUsagePermission()
  }

  const handleEnterKeyDown = (event) => {
    if (event.key === 'Enter') {
      return onSearchClick()
    }
  }

  const resetAddUsagePermission = () => {
    setFormData(newPermissionInfo)
    setFormReady(false)
    setDataChanged(false)
  }

  const addUsagePermission = async (usagePermission: UsagePermissionsInfo) => {
    checkPermissions(store.getState(), ADD_USAGE_PERMISSION)

    dispatch(beginApiCall())
    try {
      const result: UsagePermissionResponseDto =
        await addUsagePermissionAsync(usagePermission)
      if (result != null) {
        dispatch(addUsagePermissionSuccess(usagePermission))
        toast.success(t('usage_permission.usage_permission_added'))
        onSearchClick()
        setPageNum(1)
      } else {
        dispatch(endApiCall())
        toast.error(t('usage_permission.usage_permission_add_failed'))
      }
    } catch (error) {
      toast.error(t('usage_permission.usage_permission_add_failed'))
      dispatch(apiCallError())
      throw error
    }
  }

  const [open, setOpen] = React.useState(false)
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([])

  const handleClickOpen = (emailAddresses: string[]): void => {
    setOpen(true)
    setSelectedEmails(emailAddresses)
  }

  const handleClose = (): void => {
    setOpen(false)
    setSelectedEmails([])
  }

  const getEmailsDisplayHtml = (emails: string): React.JSX.Element => {
    const emailAddresses: string[] = emails.split(',')

    if (emailAddresses.length > 2) {
      const [e1, e2] = emailAddresses ? [...emailAddresses] : []
      const displayText = `${e1}, ${e2}`
      const displayLink = `(+${emailAddresses.length - 2}` + t('more') + `)`
      return (
        <div className="responsive-font">
          <span>{displayText}</span>
          <span>
            <Link
              component="button"
              variant="body2"
              onClick={() => handleClickOpen(emailAddresses)}
            >
              {displayLink}
            </Link>
          </span>
        </div>
      )
    }

    return <span className="responsive-font">{emails}</span>
  }

  const displayUsagePermissionRow = (usagePermission: UsagePermissionsInfo) => {
    const lastUsed =
      usagePermission.lastUsed != null
        ? new Date(usagePermission.lastUsed)
        : null
    const createdOn = new Date(usagePermission.timestamp)
    const updatedOn = new Date(usagePermission.updatedOn)

    return (
      <ExpandableRow rowIndex={usagePermission.usage}>
        {(isExpanded, toggleExpansion) => (
          <tr
            key={usagePermission.usage}
            className={isExpanded() ? 'expandedRow' : 'tableRow'}
          >
            <TOverlayTrigger
              tooltipId="tooltip-usage"
              tooltipVal={usagePermission.usage}
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  <div>{usagePermission.usage}</div>
                </td>
              }
            />
            <TOverlayTrigger
              tooltipId="tooltip-entraIdGroup"
              tooltipVal={
                usagePermission.entraIDGroup
                  ? usagePermission.entraIDGroup
                  : null
              }
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  <div>{usagePermission.entraIDGroup}</div>
                </td>
              }
            />
            <td className="tableCell" onClick={toggleExpansion}>
              {getEmailsDisplayHtml(usagePermission.emails)}
            </td>
            <TOverlayTrigger
              tooltipId="tooltip-lastUsed"
              tooltipVal={lastUsed != null ? lastUsed.toLocaleString() : null}
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  {lastUsed != null ? lastUsed.toLocaleDateString() : null}
                </td>
              }
            />
            <TOverlayTrigger
              tooltipId="tooltip-timestamp"
              tooltipVal={createdOn.toLocaleString()}
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  {createdOn.toLocaleDateString()}
                </td>
              }
            />
            <TOverlayTrigger
              tooltipId="tooltip-updatedOn"
              tooltipVal={updatedOn.toLocaleString()}
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  {updatedOn.toLocaleDateString()}
                </td>
              }
            />
            <td>
              <TEditLink
                disable={disableEditUsagePermissions}
                pathName={'/usagepermissions/' + usagePermission.id}
              />
            </td>
            <td>
              <TDeleteLink
                disable={disableDeleteUsagePermissions}
                title={t('usage_permission.delete')}
                onDelete={() => onDeleteClick(usagePermission)}
                itemName={usagePermission.usage}
                showNoteSoftDelete={false}
              />
            </td>
          </tr>
        )}
      </ExpandableRow>
    )
  }

  const TRow = () =>
    usagePermissionsExists ? (
      <>
        {usagePermissions
          .slice(
            (pageNum - 1) * usagePermissionsPerPage,
            pageNum * usagePermissionsPerPage,
          )
          .map((usagePermission: UsagePermissionsInfo) => {
            return displayUsagePermissionRow(usagePermission)
          })}
      </>
    ) : (
      <TRowEmpty
        colSpan="8"
        emptyRowText={t('usage_permission.no_usage_permission_found')}
      />
    )

  return (
    <>
      <div className="scrollable responsive-font">
        <Container fluid>
          <Row className="g-3">
            <Col xs={12} lg={2} className="mt-4">
              <input
                name="usage"
                type="text"
                placeholder={t('usage_permission.name')}
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleEnterKeyDown}
                value={searchPermissionsRequest.usage}
              />
            </Col>
            <Col xs={12} lg={3} className="mt-4">
              <input
                name="entraIDGroup"
                type="text"
                placeholder={t('key.aad_revocation')}
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleEnterKeyDown}
                value={searchPermissionsRequest.entraIDGroup}
              />
            </Col>
            <Col xs={12} lg={3} className="mt-4">
              <input
                name="emails"
                type="text"
                placeholder={t('email_address')}
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleEnterKeyDown}
                value={searchPermissionsRequest.emails}
              />
            </Col>
            <Col xs={12} lg={4} className="mt-4">
              <button
                onClick={onSearchClick}
                disabled={disableGetUsagePermissions}
                className={
                  `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                  'responsive-button btn text-nowrap spacing-right' +
                  (disableGetUsagePermissions
                    ? ' btn-outline-primary'
                    : ' btn-primary')
                }
              >
                {t('usage_permission.get_usage')}
              </button>{' '}
              <GenericModal
                disableModal={disableAddUsagePermissions}
                disableOkButton={!(dataChanged && formReady)}
                title={t('usage_permission.add')}
                onOk={handleSaveAddUsagePermissionModal}
                onClose={handleCloseAddUsagePermissionModal}
                modalBody={
                  <AddUsagePermissionsForm
                    ref={refValidateData}
                    entityData={formData}
                    setEntityData={setFormData}
                    setFormReady={setFormReady}
                    setDataChanged={setDataChanged}
                  />
                }
                buttonOkText={t('save')}
              >
                <button
                  disabled={disableAddUsagePermissions}
                  className={
                    `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                    'responsive-button btn text-nowrap' +
                    (disableAddUsagePermissions
                      ? ' btn-outline-success'
                      : ' btn-success')
                  }
                >
                  {t('add')}
                </button>
              </GenericModal>{' '}
            </Col>
          </Row>
        </Container>
        <table className="table table-hover responsive-font mt-3">
          <thead>
            <tr className="border-top">
              <th>{t('usage_permission.name')}</th>
              <th>{t('key.aad_revocation')}</th>
              <th>{t('usage_permission.email_address')}</th>
              <th>{t('key.last_used')}</th>
              <th>{t('key.created')}</th>
              <th>{t('usage_permission.modified')}</th>
              <th />
              <th />
            </tr>
          </thead>
          <TBody isLoading={loading} TRow={TRow} colSpan="8" />
        </table>
      </div>
      <TFooter
        show={usagePermissions.length > 0 && !loading}
        showingText={showingText}
        pageNum={pageNum}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        allowLoadMore={!disableLoadMore}
        onLoadMoreClick={onLoadMoreClick}
      />
      <DisplayMoreDialog
        id="display-more"
        showAvatar={true}
        open={open}
        onClose={handleClose}
        values={selectedEmails}
      />
    </>
  )
}

export default UsagePermissionsList
export { UsagePermissionsProps }
